import React, { useState } from 'react'
import { Clock, Save, X, Plus, Edit3, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

const API_URL = process.env.REACT_APP_BACKEND_URL

export const STATUTS_DIFFERE = {
  en_attente_facture: { label: 'En attente de facture', cls: 'bg-amber-100 text-amber-800' },
  facture_transmise: { label: 'Facture transmise à la compagnie', cls: 'bg-blue-100 text-blue-800' },
  percue: { label: 'Perçue', cls: 'bg-emerald-100 text-emerald-800' },
  abandonnee: { label: 'Abandonnée', cls: 'bg-slate-200 text-slate-600' },
}

const fmt = (v) => (v == null || v === '' || isNaN(v)) ? '—' : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v)
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—'

export default function IndemniteDiffereeBlock({ sinistre, onRefresh, onEvent, readOnly }) {
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({})

  const statut = sinistre.indemnite_differee_statut
  const enCours = statut === 'en_attente_facture' || statut === 'facture_transmise'
  const depassee = enCours && sinistre.indemnite_differee_date_limite && new Date(sinistre.indemnite_differee_date_limite) < new Date()

  const startEdit = () => {
    setForm({
      indemnite_differee_montant: sinistre.indemnite_differee_montant ?? '',
      indemnite_differee_statut: statut || 'en_attente_facture',
      indemnite_differee_date_limite: sinistre.indemnite_differee_date_limite || '',
      indemnite_differee_montant_percu: sinistre.indemnite_differee_montant_percu ?? '',
      indemnite_differee_date_perception: sinistre.indemnite_differee_date_perception || '',
    })
    setEditing(true)
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  async function save() {
    if (form.indemnite_differee_montant === '' || isNaN(parseFloat(form.indemnite_differee_montant))) {
      toast.error('Saisissez le montant de l\'indemnité différée')
      return
    }
    if (form.indemnite_differee_statut === 'percue' && (form.indemnite_differee_montant_percu === '' || isNaN(parseFloat(form.indemnite_differee_montant_percu)))) {
      toast.error('Saisissez le montant perçu')
      return
    }
    setSaving(true)
    try {
      const percue = form.indemnite_differee_statut === 'percue'
      const payload = {
        indemnite_differee_montant: parseFloat(form.indemnite_differee_montant),
        indemnite_differee_statut: form.indemnite_differee_statut,
        indemnite_differee_date_limite: form.indemnite_differee_date_limite || null,
        indemnite_differee_montant_percu: percue ? parseFloat(form.indemnite_differee_montant_percu) : null,
        indemnite_differee_date_perception: percue ? (form.indemnite_differee_date_perception || new Date().toISOString().slice(0, 10)) : null,
      }
      const res = await fetch(`${API_URL}/api/sinistres/${sinistre.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Erreur')
      const lbl = STATUTS_DIFFERE[payload.indemnite_differee_statut].label
      onEvent?.({
        type_evenement: percue ? 'paiement' : 'provision_mise_a_jour',
        titre: percue ? 'Indemnité différée perçue' : 'Indemnité différée mise à jour',
        contenu: percue
          ? `Indemnité différée perçue : ${fmt(payload.indemnite_differee_montant_percu)} le ${fmtDate(payload.indemnite_differee_date_perception)} (attendue ${fmt(payload.indemnite_differee_montant)})`
          : `Indemnité différée de ${fmt(payload.indemnite_differee_montant)} — ${lbl}${payload.indemnite_differee_date_limite ? ` — facture à présenter avant le ${fmtDate(payload.indemnite_differee_date_limite)}` : ''}`,
        nouvelle_valeur: payload,
      })
      toast.success('Indemnité différée enregistrée')
      setEditing(false)
      onRefresh?.()
    } catch (e) {
      toast.error(e.message || 'Erreur lors de l\'enregistrement')
    } finally {
      setSaving(false)
    }
  }

  if (!statut && !editing) {
    if (readOnly) return null
    return (
      <button onClick={startEdit} data-testid="add-indemnite-differee-btn"
        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-slate-600 border border-dashed border-slate-300 rounded-lg hover:border-slate-400 hover:text-slate-800 transition-colors">
        <Plus size={14} /> Ajouter une indemnité différée
      </button>
    )
  }

  if (editing) {
    return (
      <div className="border border-slate-200 rounded-lg p-3 space-y-3 bg-slate-50" data-testid="indemnite-differee-form">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 flex items-center gap-1.5"><Clock size={12} /> Indemnité différée</div>
        <Field label="Montant attendu (€)">
          <input type="number" value={form.indemnite_differee_montant} onChange={e => set('indemnite_differee_montant', e.target.value)} data-testid="differe-montant-input" className={inputCls} autoFocus />
        </Field>
        <Field label="Facture à présenter avant le">
          <input type="date" value={form.indemnite_differee_date_limite} onChange={e => set('indemnite_differee_date_limite', e.target.value)} data-testid="differe-date-limite-input" className={inputCls} />
        </Field>
        <Field label="Statut">
          <select value={form.indemnite_differee_statut} onChange={e => set('indemnite_differee_statut', e.target.value)} data-testid="differe-statut-select" className={inputCls}>
            {Object.entries(STATUTS_DIFFERE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </Field>
        {form.indemnite_differee_statut === 'percue' && (
          <>
            <Field label="Montant perçu (€)">
              <input type="number" value={form.indemnite_differee_montant_percu} onChange={e => set('indemnite_differee_montant_percu', e.target.value)} data-testid="differe-montant-percu-input" className={inputCls} />
            </Field>
            <Field label="Date de perception">
              <input type="date" value={form.indemnite_differee_date_perception} onChange={e => set('indemnite_differee_date_perception', e.target.value)} data-testid="differe-date-perception-input" className={inputCls} />
            </Field>
          </>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1" data-testid="differe-cancel-btn"><X size={12} /> Annuler</button>
          <button onClick={save} disabled={saving} data-testid="differe-save-btn"
            className="px-3 py-1.5 text-xs font-medium bg-slate-800 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50 flex items-center gap-1">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Enregistrer
          </button>
        </div>
      </div>
    )
  }

  const st = STATUTS_DIFFERE[statut]
  return (
    <div className={`border rounded-lg p-3 space-y-2 ${depassee ? 'border-red-300 bg-red-50' : enCours ? 'border-amber-200 bg-amber-50/40' : 'border-slate-200'}`} data-testid="indemnite-differee-block">
      <div className="flex justify-between items-center">
        <span className="text-sm text-slate-500 flex items-center gap-1.5"><Clock size={13} /> Indemnité différée</span>
        <span className="font-mono text-sm font-semibold text-slate-700" data-testid="differe-montant">{fmt(sinistre.indemnite_differee_montant)}</span>
      </div>
      <div className="flex justify-between items-center gap-2">
        <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${st.cls}`} data-testid="differe-statut-badge">{st.label}</span>
        {!readOnly && (
          <button onClick={startEdit} className="text-slate-400 hover:text-slate-700 p-1" data-testid="edit-indemnite-differee-btn" title="Modifier"><Edit3 size={13} /></button>
        )}
      </div>
      {enCours && sinistre.indemnite_differee_date_limite && (
        <div className={`text-xs ${depassee ? 'text-red-700 font-medium' : 'text-slate-500'}`} data-testid="differe-date-limite">
          {depassee ? 'Délai dépassé — facture attendue avant le ' : 'Facture à présenter avant le '}{fmtDate(sinistre.indemnite_differee_date_limite)}
        </div>
      )}
      {statut === 'percue' && (
        <div className="flex justify-between items-center text-xs text-slate-500">
          <span>Perçue le {fmtDate(sinistre.indemnite_differee_date_perception)}</span>
          <span className="font-mono font-semibold text-emerald-600" data-testid="differe-montant-percu">{fmt(sinistre.indemnite_differee_montant_percu)}</span>
        </div>
      )}
    </div>
  )
}

const inputCls = 'w-full h-9 px-2.5 text-sm border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-slate-400 outline-none'

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-medium text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  )
}
